/* eslint-disable */
// src/components/VisitTransactionsDetails/visitTransactionsView.js

import React, { useMemo } from "react";
import { Card, Box, TextField, MenuItem, Button } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import { Link, useLocation } from "react-router-dom";
import { getVisitDetailsDestination } from "utils/navigateVisitDetailsHelper";
import { formatVisitIncomeDate } from "utils/visitIncomeDate";
import { moneyFmtRoundedDynamic as moneyFmt } from "utils/formatters";
import {
  paginationContainedButtonSx,
  secondaryOutlinedButtonSx,
} from "components/common/buttonStyles";

export default function VisitTransactionsView({
  rows = [],
  loading = false,
  title = "Ingresos",
  showTitle = true,
  counterNoun = "ingresos",
  returnToLabel = "Volver a ingresos",
  emptyMessage = "Sin resultados.",
  sortBy = "date",
  sortDir = "DESC",
  onSortByChange = () => {},
  onSortDirChange = () => {},
  query = "",
  onQueryChange = () => {},
  filters = {},
  page = 1,
  pageSize = 10,
  pagination = null,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  searchText = "",
  onSearchTextChange = () => {},
  appliedQuery = "",
  serverSide = false,
  showSearch = true,
  searchLabel = "Buscar",
  searchPlaceholder = null,
  searchFieldMinWidth = 220,
  showSortControls = true,
  showLocalColumn = true,
  sortOptions = [
    { value: "date", label: "Fecha" },
    { value: "local", label: "Local" },
  ],
}) {
  const money = (n) => moneyFmt.format(n ?? 0);
  const location = useLocation();
  const availableSortOptions =
    Array.isArray(sortOptions) && sortOptions.length > 0
      ? sortOptions
      : [{ value: "date", label: "Fecha" }];
  const shouldShowSortControls = showSortControls && availableSortOptions.length > 1;
  const resolvedSearchPlaceholder = searchPlaceholder || (showLocalColumn
    ? "Buscar por ID local, ID visita, local o ciudad..."
    : "Buscar por ID visita...");

  const returnTo = useMemo(
    () => ({
      pathname: location.pathname,
      search: location.search || "",
      hash: location.hash || "",
      label: returnToLabel,
      state: location.state ?? null,
      context: showLocalColumn ? "general" : "local",
      viewState: { sortBy, sortDir: "DESC", query: appliedQuery || query, searchText, filters, page, pageSize },
    }),
    [
      location.pathname,
      location.search,
      location.hash,
      location.state,
      returnToLabel,
      showLocalColumn,
      sortBy,
      sortDir,
      query,
      appliedQuery,
      searchText,
      filters,
      page,
      pageSize,
    ]
  );

  const filteredRows = useMemo(() => {
    if (serverSide) return rows;
    const q = (query || "").toLowerCase().trim();
    if (!q) return rows;
    if (showLocalColumn) {
      return rows.filter((r) =>
        String(r.VisitId ?? "").includes(q) ||
        String(r.LocalId ?? "").includes(q) ||
        (r.LocalName || "").toLowerCase().includes(q) ||
        (r.LocalCity || "").toLowerCase().includes(q)
      );
    }
    return rows.filter((r) => String(r.VisitId ?? "").includes(q));
  }, [rows, query, showLocalColumn, serverSide]);

  const visibleRows = serverSide ? rows : filteredRows;
  const effectiveEmptyMessage =
    serverSide && appliedQuery && Number(pagination?.totalRows ?? 0) === 0
      ? "No hay resultados para la búsqueda."
      : emptyMessage;

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "Fecha",
        id: "collectionDate",
        accessor: (row) => formatVisitIncomeDate(row),
        Cell: ({ row, value }) => {
          const income = row.original;
          const destination = getVisitDetailsDestination(
            income?.VisitId,
            income?.LocalId,
            undefined,
            income,
            returnTo
          );
          if (!destination) return value;
          return (
            <Box
              component={Link}
              to={destination.pathname}
              state={destination.state}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: 44,
                color: "info.main",
                textDecoration: "underline",
                textUnderlineOffset: "2px",
                "&:hover": { color: "info.dark" },
                "&:focus-visible": { outline: "2px solid", outlineColor: "info.main", outlineOffset: 2 },
              }}
            >
              {value}
            </Box>
          );
        },
      },
    ];

    if (showLocalColumn) {
      baseColumns.push({
        Header: "Local",
        accessor: "LocalName",
        Cell: ({ row }) => {
          const ownerName = row.original?.OwnerName?.trim();
          return (
            <Box sx={{ display: "flex", flexDirection: "column", whiteSpace: "normal", minWidth: 140 }}>
              {ownerName && <Box component="span" sx={{ fontWeight: 600 }}>{ownerName}</Box>}
              <Box component="span">{row.original?.LocalName || "-"}</Box>
            </Box>
          );
        },
      });
      baseColumns.push({ Header: "Ciudad", accessor: "LocalCity" });
    }

    baseColumns.push(
      { Header: "Total", accessor: "TotalAmount", Cell: ({ value }) => money(value) },
      { Header: "Cliente", accessor: "TotalClientAmount", Cell: ({ value }) => money(value) },
      { Header: "Empresa", accessor: "TotalOwnerAmount", Cell: ({ value }) => money(value) }
    );

    return baseColumns;
  }, [showLocalColumn, returnTo]);

  return (
    <Card>
      {(showTitle || shouldShowSortControls || showSearch) && <MDBox
        display="flex"
        justifyContent={showTitle ? "space-between" : "flex-end"}
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        {showTitle && <MDTypography variant="h5">{title}</MDTypography>}

        <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
          {shouldShowSortControls && (
            <>
              <TextField
                size="small"
                label="Ordenar por"
                select
                value={sortBy}
                onChange={(e) => onSortByChange(e.target.value)}
                sx={{ minWidth: 140, height: 40, "& .MuiInputBase-root": { height: 40 } }}
              >
                {availableSortOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          {showSearch &&
            (serverSide ? (
              <TextField
                size="small"
                label={searchLabel}
                placeholder={resolvedSearchPlaceholder}
                value={searchText}
                onChange={(e) => onSearchTextChange(e.target.value)}
                sx={{
                  minWidth: { xs: "100%", sm: searchFieldMinWidth },
                  height: 40,
                  "& .MuiInputBase-root": { height: 40 },
                }}
              />
            ) : (
              <TextField
                size="small"
                label="Buscar"
                placeholder={resolvedSearchPlaceholder}
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                sx={{ minWidth: 200, height: 40, "& .MuiInputBase-root": { height: 40 } }}
              />
            ))}
        </MDBox>
      </MDBox>}

      <MDBox
        px={2}
        pb={2}
        sx={(theme) => ({
          "& table": { minWidth: showLocalColumn ? 780 : 640 },
          "& thead th:first-of-type, & tbody td:first-of-type": {
            position: "sticky",
            left: 0,
            minWidth: 160,
            width: 160,
            backgroundColor: theme.palette.background.paper,
            boxShadow: `1px 0 0 ${theme.palette.divider}`,
          },
          "& thead th:first-of-type": { zIndex: 3 },
          "& tbody td:first-of-type": { zIndex: 2 },
        })}
      >
        <DataTable
          table={{ columns, rows: visibleRows }}
          isSorted={false}
          entriesPerPage={false}
          canSearch={false}
          showTotalEntries={!serverSide}
          disablePagination={serverSide}
        />
        {loading && (
          <Box p={2}>
            <MDTypography variant="button">Cargando...</MDTypography>
          </Box>
        )}
        {!loading && visibleRows.length === 0 && (
          <Box p={2}>
            <MDTypography variant="button">{effectiveEmptyMessage}</MDTypography>
          </Box>
        )}
        {serverSide && !loading && pagination && (
          <MDBox
            px={1}
            pt={2}
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            flexDirection={{ xs: "column", md: "row" }}
            gap={2}
          >
            <MDTypography variant="button" color="secondary">
              {`Mostrando ${visibleRows.length} de ${pagination.totalRows || 0} ${counterNoun}`}
            </MDTypography>

            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <MDTypography variant="button" color="secondary">
                {`Página ${pagination.totalPages > 0 ? page : 0} de ${pagination.totalPages || 0}`}
              </MDTypography>
              <TextField
                select
                size="small"
                label="Filas"
                value={String(pageSize)}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                sx={{ minWidth: 100, "& .MuiInputBase-root": { height: 40 } }}
              >
                {[10, 25, 50].map((size) => (
                  <MenuItem key={size} value={String(size)}>
                    {size}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                color="info"
                disabled={!pagination.hasPreviousPage}
                sx={secondaryOutlinedButtonSx}
                onClick={() => onPageChange(page - 1)}
              >
                Anterior
              </Button>
              <Button
                variant="contained"
                color="info"
                disabled={!pagination.hasNextPage}
                sx={paginationContainedButtonSx}
                onClick={() => onPageChange(page + 1)}
              >
                Siguiente
              </Button>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}
